import type { EventBus } from './eventBus';

export type ChargeType = 'thrown' | 'rocket' | 'homing' | 'none';

export interface ChargeCallbacks {
  playChargeSound: (type: 'thrown' | 'rocket') => void;
  stopChargeSound: () => void;
  emitChargeParticles: (x: number, y: number) => void;
}

export class ChargeStateManager {
  private chargePower = 0;
  private fuseTimer = 3;
  private chargeSoundPlaying = false;
  private eventBus: EventBus | null = null;
  private callbacks: ChargeCallbacks | null = null;

  private chargeRate = 100;
  private readonly MAX_CHARGE = 100;
  private readonly MIN_FUSE = 1;
  private readonly MAX_FUSE = 5;

  constructor(eventBus?: EventBus) {
    this.eventBus = eventBus ?? null;
  }

  setEventBus(eventBus: EventBus): void {
    this.eventBus = eventBus;
  }

  setCallbacks(callbacks: ChargeCallbacks): void {
    this.callbacks = callbacks;
  }

  /** Power units gained per second of charging (mode-configurable). */
  setChargeRate(rate: number): void {
    if (Number.isFinite(rate) && rate > 0) {
      this.chargeRate = rate;
    }
  }

  update(
    deltaTime: number,
    isCharging: boolean,
    chargeType: ChargeType,
    wormPosition?: { x: number; y: number },
  ): void {
    if (isCharging) {
      const wasCharging = this.chargePower > 0;
      this.chargePower = Math.min(this.MAX_CHARGE, this.chargePower + this.chargeRate * deltaTime);

      if (chargeType !== 'none') {
        if (!this.chargeSoundPlaying) {
          const soundType = chargeType === 'thrown' ? 'thrown' : 'rocket';
          this.callbacks?.playChargeSound(soundType);
          this.chargeSoundPlaying = true;
        }

        if (wasCharging && wormPosition) {
          this.callbacks?.emitChargeParticles(wormPosition.x, wormPosition.y);
        }
      }
    } else if (this.chargeSoundPlaying) {
      this.callbacks?.stopChargeSound();
      this.chargeSoundPlaying = false;
    }
  }

  startCharging(): void {
    this.chargePower = 0;
  }

  stopCharging(): void {
    if (this.chargeSoundPlaying) {
      this.callbacks?.stopChargeSound();
      this.chargeSoundPlaying = false;
    }
  }

  releaseCharge(): number {
    const power = this.chargePower;
    this.chargePower = 0;
    if (this.chargeSoundPlaying) {
      this.callbacks?.stopChargeSound();
      this.chargeSoundPlaying = false;
    }
    return power;
  }

  setInstantCharge(power: number): void {
    this.chargePower = Math.min(this.MAX_CHARGE, Math.max(0, power));
  }

  reset(): void {
    this.chargePower = 0;
    if (this.chargeSoundPlaying) {
      this.callbacks?.stopChargeSound();
      this.chargeSoundPlaying = false;
    }
  }

  cycleFuseTimer(): void {
    this.fuseTimer = (this.fuseTimer % this.MAX_FUSE) + 1;
    this.eventBus?.emit('weapon:fuseChanged', { timer: this.fuseTimer });
  }

  setFuseTimer(timer: number): void {
    this.fuseTimer = Math.max(this.MIN_FUSE, Math.min(this.MAX_FUSE, timer));
  }

  getChargePower(): number {
    return this.chargePower;
  }

  getFuseTimer(): number {
    return this.fuseTimer;
  }

  hasCharge(): boolean {
    return this.chargePower > 0;
  }

  getChargePercent(): number {
    return this.chargePower / this.MAX_CHARGE;
  }
}
